import React, { useEffect, useState } from "react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { getSecureData, notify, setSecureData, validatePassword } from "../../helpers";
import { Eye, EyeOff, Loader, Lock, LogOut } from "lucide-react";
import Spacing from "../Spacing";
import { useNavigate } from "react-router-dom";
import { Config } from "../../Config";
import { requiredChangePassword } from "../../services/users";
import { getPracticeInfo } from "../../Session";
import { useLogout } from "../../hooks/useLogout";

export default function UpdatePassword() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const navigate = useNavigate();
  let useInfo = getPracticeInfo();
  
  const [enableLoader, setEnableLoader] = useState(false);
  const [oldPassword, setOldPassword] = useState(false);
  const [newPassword, setNewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isValid, setIsValid] = useState(null);
  const portalLogout = useLogout();

  const [informations, setInformations] = useState({
    account_id: useInfo?.account_id || null,
    location_id: useInfo?.location_id || null,
    user_id: useInfo?.user_id || null,
    user_name: useInfo?.user_name || "",
    email: useInfo?.email || "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    setInformations((prev) => ({
      ...prev,
      account_id: useInfo?.account_id || null,
      location_id: useInfo?.location_id || null,
      user_id: useInfo?.user_id || null,
      user_name: useInfo?.user_name || "",
      email: useInfo?.email || "",
    }));
  }, [useInfo]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInformations({
      ...informations,
      [name]: value
    });

    if(name === 'new_password' || name === 'confirm_password') {
      let validityCheck = validatePassword(value);
      if(name === 'new_password' && !validityCheck) {
        setIsValid('new_password');
      } else if(name === 'confirm_password' && !validityCheck) {
        setIsValid('confirm_password');
      } else {
        setIsValid(null);
      }
    } else {
      setIsValid(null);
    }
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnableLoader(true);

    if (informations.new_password !== "" && !validatePassword(informations.new_password)) {
      setEnableLoader(false);
      setIsValid(null);
      return;
    }

    if (informations.current_password === "" && informations.new_password !== "" && informations.confirm_password !== "") {
      setError("For change password you need to provide current password.");
      setEnableLoader(false);
      return;
    }
    if (informations.current_password !== "" && informations.new_password !== "" && informations.confirm_password !== "") {
      if (informations.new_password !== informations.confirm_password) {
        setError("New password and confirm password do not match.");
        setEnableLoader(false);
        return;
      }
    }

    setError("");

    try {
      const result = await requiredChangePassword(informations)

      if(result?.detail) {
        notify("error", result?.detail);
      }
      if (result.data.status === 'success') {
        // Update local data
        let prevPracticeInfo = {};
        try {
          const stored = await getSecureData("practiceInfo");
          if (stored) prevPracticeInfo = JSON.parse(stored);
        } catch (e) {}
        const updatedPracticeInfo = {
          ...prevPracticeInfo,
          require_change_password: false,
        };
        await setSecureData("practiceInfo", JSON.stringify(updatedPracticeInfo));

        notify('success', result?.data?.message);
        navigate("/resume-setup", { replace: true });
      } else {
        notify('error', result?.data?.message);
      }
    } catch (error) {
      notify("error", "Something went wrong", 2);
    } finally {
      setEnableLoader(false);
    }
  };

  useDocumentTitle("Update Password");

  return (
    <div className="docpro_login_wrap relative" style={{ height: "100vh" }}>
      <div className="docpro_login_right">
        <div className="docpro_login_card_wrap" style={{ flexDirection: "column", justifyContent: "start" }}>
          <div className="docpro_veradigm_connection_header">
            <div className="flex items-center docpro_grid_gap_10 docpro_ehr_buttons">
              <button
                type="button"
                onClick={portalLogout}
                className="docpro_bordered_btn docpro_fw_500 docpro_white_color docpro_filed_btn size_lg"
                style={{ backgroundColor: '#e0483d', borderColor: '#c33f35' }}
              >
                <LogOut width={15} height={15} />
                <span>Log Out</span>
              </button>
            </div>
            <Spacing lg="45" sm="50" />
            <div className="docpro_text_center">
              <img src={`${Config.appUrl}/images/logo_2.svg`} alt="Logo" />
              <Spacing lg="32" sm="32" />
              <h1 className="docpro_mb_0">Update your password</h1>
            </div>
            <Spacing lg="40" sm="40" />
          </div>

          <div className="docpro_login_card">
            <form onSubmit={handleSubmit} autoComplete="false">
              <label className="docpro_fw_500 docpro_primary_color">
                Email <span className="docpro_red_color">*</span>
              </label>
              <input
                type="email"
                className="docpro_form_field size_lg"
                placeholder="Enter your email"
                name="email"
                required
                value={informations.email}
                onChange={handleChange}
              />
              <Spacing lg="15" sm="20" />

              <label className="docpro_fw_500 docpro_primary_color"> Current Password <span className="docpro_red_color">*</span></label>
              <div className="docpro_form_field_with_icon">
                <input
                  name="current_password"
                  type={oldPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="docpro_form_field size_lg"
                  onChange={handleChange}
                  required
                  minLength={4}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon" />
                { oldPassword ?
                  <Eye onClick={()=>setOldPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: '#b0b0b0' }} />
                :
                  <EyeOff onClick={()=>setOldPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: '#b0b0b0' }} />
                }
              </div>
              <Spacing lg="15" sm="20" />

              <label className="docpro_fw_500 docpro_primary_color"> New Password <span className="docpro_red_color">*</span></label>
              <div className="docpro_form_field_with_icon">
                <input
                  name="new_password"
                  type={newPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  className={`docpro_form_field size_lg ${(isValid === 'new_password') && 'docpro_border_red'}`}
                  onChange={handleChange}
                  required
                  minLength={12}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon" />
                { newPassword ?
                  <Eye onClick={()=>setNewPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: '#b0b0b0' }} />
                :
                  <EyeOff onClick={()=>setNewPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: '#b0b0b0' }} />
                }
              </div>
              <Spacing lg="15" sm="20" />

              <label className="docpro_fw_500 docpro_primary_color"> Confirm Password <span className="docpro_red_color">*</span></label>
              <div className="docpro_form_field_with_icon">
                <input
                  name="confirm_password"
                  type={confirmPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  className={`docpro_form_field size_lg ${(isValid === 'confirm_password') && 'docpro_border_red'}`}
                  onChange={handleChange}
                  required
                  minLength={12}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon" />
                { confirmPassword ?
                  <Eye onClick={()=>setConfirmPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: '#b0b0b0' }} />
                :
                  <EyeOff onClick={()=>setConfirmPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: '#b0b0b0' }} />
                }
              </div>
              <Spacing lg="10" sm="10" />
              {error && <div className="text-red-500 text-sm">{error}</div>}
              {(isValid) && (
                <div className="text-red-500 text-sm">
                  Password must be at least 12 characters and include uppercase, lowercase, number, and special character.
                </div>
              )}
              <Spacing lg="25" sm="25" />
              <button
                type="submit"
                className="docpro_bordered_btn size_lg width_100 docpro_fw_600 docpro_white_color docpro_filed_btn"
              >
                <span>Change Password</span>
                {enableLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </form>
            <Spacing lg="30" sm="30" />
          </div>
        </div>
        <footer className="docpro_login_footer pb-1">
          <p>© {currentYear} DocPro. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
